
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Download, Receipt, ShoppingCart, Package, MapPin, Phone, Mail } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

interface PurchaseReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseRequestId: string;
}

interface ReceiptData {
  purchase_date: string;
  product_title: string;
  product_price: number;
  product_description?: string;
  quantity: number;
  total_amount: number;
  pickup_address?: string;
  professional_name: string;
  professional_email?: string;
  professional_phone?: string;
  professional_address?: string;
  buyer_name: string;
  buyer_email?: string;
  receipt_number: string;
  notes?: string;
}

interface PurchaseReceipt {
  id: string;
  receipt_number: string;
  generated_at: string;
  receipt_data: ReceiptData;
}

export const PurchaseReceiptDialog = ({ 
  open, 
  onOpenChange, 
  purchaseRequestId 
}: PurchaseReceiptDialogProps) => {
  const { data: receipt, isLoading } = useQuery({
    queryKey: ["purchase-receipt", purchaseRequestId],
    queryFn: async () => {
      console.log('Fetching purchase receipt for request:', purchaseRequestId);

      const { data, error } = await supabase
        .from("purchase_request_receipts")
        .select("*")
        .eq("purchase_request_id", purchaseRequestId)
        .single();

      if (error) {
        console.error("Error fetching purchase receipt:", error);
        if (error.code === 'PGRST116') {
          // No receipt found
          return null;
        }
        throw error;
      }

      console.log('Receipt data fetched:', data);
      return {
        ...data,
        receipt_data: data.receipt_data as unknown as ReceiptData
      } as PurchaseReceipt;
    },
    enabled: open && !!purchaseRequestId,
  });

  const handleDownload = () => {
    if (!receipt) return;

    const receiptText = `
REÇU DE DEMANDE D'ACHAT
${receipt.receipt_data.receipt_number}

Date d'émission: ${format(new Date(receipt.generated_at), "PPP", { locale: fr })}

INFORMATIONS DE L'ACHAT
Produit: ${receipt.receipt_data.product_title}
Description: ${receipt.receipt_data.product_description || 'Non spécifiée'}
Prix unitaire: ${receipt.receipt_data.product_price}€
Quantité: ${receipt.receipt_data.quantity}
Total: ${receipt.receipt_data.total_amount}€
Date d'achat: ${format(new Date(receipt.receipt_data.purchase_date), "PPPp", { locale: fr })}

VENDEUR
Nom: ${receipt.receipt_data.professional_name}
${receipt.receipt_data.professional_email ? `Email: ${receipt.receipt_data.professional_email}` : ''}
${receipt.receipt_data.professional_phone ? `Téléphone: ${receipt.receipt_data.professional_phone}` : ''}
${receipt.receipt_data.professional_address ? `Adresse: ${receipt.receipt_data.professional_address}` : ''}

ACHETEUR
Nom: ${receipt.receipt_data.buyer_name}
${receipt.receipt_data.buyer_email ? `Email: ${receipt.receipt_data.buyer_email}` : ''}

LIVRAISON/RÉCUPÉRATION
${receipt.receipt_data.pickup_address || 'Adresse non spécifiée'}

${receipt.receipt_data.notes ? `NOTES: ${receipt.receipt_data.notes}` : ''}

Ce reçu confirme votre achat approuvé.
    `.trim();

    const blob = new Blob([receiptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recu-achat-${receipt.receipt_data.receipt_number}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Reçu téléchargé avec succès");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Reçu de demande d'achat
          </DialogTitle>
          <DialogDescription>
            Détails du reçu pour votre achat approuvé
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : receipt ? (
          <div className="space-y-6">
            {/* En-tête du reçu */}
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-xl">REÇU DE DEMANDE D'ACHAT</CardTitle>
                <Badge variant="outline" className="self-center">
                  {receipt.receipt_data.receipt_number}
                </Badge>
                <p className="text-sm text-muted-foreground">
                  Émis le {format(new Date(receipt.generated_at), "PPP", { locale: fr })}
                </p>
              </CardHeader>
            </Card>

            {/* Informations du produit */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Informations du produit
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Produit:</span>
                  <span>{receipt.receipt_data.product_title}</span>
                </div>
                {receipt.receipt_data.product_description && (
                  <div className="flex justify-between">
                    <span className="font-medium">Description:</span>
                    <span className="text-right max-w-[60%]">{receipt.receipt_data.product_description}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-medium">Prix unitaire:</span>
                  <span>{receipt.receipt_data.product_price}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Quantité:</span>
                  <span>{receipt.receipt_data.quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Date d'achat:</span>
                  <span>
                    {format(new Date(receipt.receipt_data.purchase_date), "PPPp", { locale: fr })}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-green-600">{receipt.receipt_data.total_amount}€</span>
                </div>
              </CardContent>
            </Card>

            {/* Informations du vendeur */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Vendeur</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Nom:</span>
                  <span>{receipt.receipt_data.professional_name}</span>
                </div>
                {receipt.receipt_data.professional_email && (
                  <div className="flex justify-between">
                    <span className="font-medium flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      Email:
                    </span>
                    <span>{receipt.receipt_data.professional_email}</span>
                  </div>
                )}
                {receipt.receipt_data.professional_phone && (
                  <div className="flex justify-between">
                    <span className="font-medium flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      Téléphone:
                    </span>
                    <span>{receipt.receipt_data.professional_phone}</span>
                  </div>
                )}
                {receipt.receipt_data.professional_address && (
                  <div className="flex justify-between">
                    <span className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      Adresse:
                    </span>
                    <span className="text-right">{receipt.receipt_data.professional_address}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Informations de l'acheteur */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Acheteur</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Nom:</span>
                  <span>{receipt.receipt_data.buyer_name}</span>
                </div>
                {receipt.receipt_data.buyer_email && (
                  <div className="flex justify-between">
                    <span className="font-medium flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      Email:
                    </span>
                    <span>{receipt.receipt_data.buyer_email}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Adresse de livraison/récupération */}
            {receipt.receipt_data.pickup_address && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Livraison/Récupération
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{receipt.receipt_data.pickup_address}</p>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {receipt.receipt_data.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{receipt.receipt_data.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Bouton de téléchargement */}
            <div className="flex justify-center">
              <Button onClick={handleDownload} className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Télécharger le reçu
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center p-8">
            <p className="text-muted-foreground">
              Aucun reçu trouvé pour cette demande d'achat.
              {purchaseRequestId && (
                <span className="block text-sm mt-2">
                  Le reçu sera généré automatiquement quand la demande sera approuvée.
                </span>
              )}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
